import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Store } from '@/types/models/Store';
import { StoreService, CustomerOrderService } from '@/services';
import { logger } from '@/lib/logger';
import { AppConfig, activeAppConfig, mergeAppConfig } from '@/constants/appConfig';
import { StoreConfig, DEFAULT_STORE_CONFIG, mergeStoreConfig } from '@/types/models/StoreConfig';
import { normalizeStoreType } from '@/constants/storeTypes';

// Types
interface StoreAdmin {
  id: string;
  role: string;
  phone?: string;
  email?: string;
}

interface StoreAppConfigState {
  config: AppConfig | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface StoresState {
  isCurrentStoreLoading: boolean;
  currentStore: Store | null;
  currentStoreId: string | null;
  error: string | null;
  currentStoreAppConfig: StoreAppConfigState;
  stores: Store[];
  isStoresLoading: boolean;
  isDeletingStore: boolean; // Add loading state for delete operation
  // Store admins state
  currentStoreAdmins: Record<string, StoreAdmin[]>; // storeId -> admins
  currentStoreAdminsLoading: Record<string, boolean>; // storeId -> loading state
  currentStoreAdminsError: Record<string, string | null>; // storeId -> error
  // Recent placed and processing order counts by store ID (for badge display)
  recentPlacedAndProcessingOrderCounts: Record<string, { count: number; lastFetched: string | null }>;
  recentPlacedAndProcessingOrderCountsLoading: Record<string, boolean>; // storeId -> loading state
}

// Initial State
const initialState: StoresState = {
  isCurrentStoreLoading: false,
  currentStore: null,
  currentStoreId: null,
  error: null,
  currentStoreAppConfig: {
    config: null,
    isLoading: false,
    error: null,
    lastUpdated: null,
  },
  stores: [],
  isStoresLoading: false,
  isDeletingStore: false,
  currentStoreAdmins: {},
  currentStoreAdminsLoading: {},
  currentStoreAdminsError: {},
  recentPlacedAndProcessingOrderCounts: {},
  recentPlacedAndProcessingOrderCountsLoading: {},
};

// Async Thunks
export const fetchStores = createAsyncThunk(
  'stores/fetchStores',
  async (userId: string) => {
    try {
      logger.info('Fetching stores where user is admin', { userId });
      return await StoreService.getAllByAdmin(userId);
    } catch (error) {
      logger.error('Failed to fetch stores by admin', { error, userId });
      throw error;
    }
  }
);

export const fetchStoreById = createAsyncThunk(
  'stores/fetchById',
  async (id: string, { getState }) => {
    try {
      logger.info('Fetching store by ID', { id });
      const state = getState() as any;
      const userId = state.auth?.user?.id;
      const store = await StoreService.getById(id, userId);
      if (!store) {
        throw new Error('Store not found');
      }
      return store;
    } catch (error) {
      logger.error('Failed to fetch store by ID', { error, id });
      throw error;
    }
  }
);

export const createStore = createAsyncThunk(
  'stores/create',
  async (data: { name: string; address?: string; phone?: string; language_locale?: string; store_type?: string; store_config?: any }) => {
    try {
      logger.info('Creating store', { data });
      return await StoreService.create(data);
    } catch (error) {
      logger.error('Failed to create store', { error, data });
      throw error;
    }
  }
);

export const updateStore = createAsyncThunk(
  'stores/update',
  async ({ id, data }: { id: string; data: any }) => {
    try {
      logger.info('Updating store', { id, data });
      if (data.units) {
        return await StoreService.updateUnits(id, data.units);
      }
      return await StoreService.update(id, data);
    } catch (error) {
      logger.error('Failed to update store', { error, id, data });
      throw error;
    }
  }
);

export const deleteStore = createAsyncThunk(
  'stores/delete',
  async (id: string) => {
    try {
      logger.info('Deleting store', { id });
      await StoreService.delete(id);
      return id;
    } catch (error) {
      logger.error('Failed to delete store', { error, id });
      throw error;
    }
  }
);

export const addStoreCategories = createAsyncThunk(
  'stores/addCategories',
  async ({ storeId, newCategories }: { storeId: string; newCategories: string[] }, { getState }) => {
    try {
      if (!newCategories || newCategories.length === 0) {
        return { newCategories: [] };
      }

      logger.info('Adding categories to store', { storeId, newCategories });
      
      // Update database
      await StoreService.addCategories(storeId, newCategories);
      
      // Return the new categories to update state
      return { newCategories };
    } catch (error) {
      logger.error('Failed to add store categories', { error, storeId, newCategories });
      throw error;
    }
  }
);

// Fetch recent placed and processing order count (from last 7 days)
// Only fetches if not already loaded or if data is stale (older than 5 minutes)
export const fetchRecentPlacedAndProcessingOrderCount = createAsyncThunk(
  'stores/fetchRecentPlacedAndProcessingOrderCount',
  async (storeId: string, { getState, rejectWithValue }) => {
    try {
      if (!storeId || typeof storeId !== 'string') {
        throw new Error('Invalid store ID');
      }

      const state = getState() as { stores: StoresState };
      const existingData = state.stores.recentPlacedAndProcessingOrderCounts[storeId];
      
      // Check if data exists and is fresh (less than 5 minutes old)
      if (existingData?.lastFetched) {
        const lastFetched = new Date(existingData.lastFetched);
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
        
        if (lastFetched > fiveMinutesAgo) {
          // Data is fresh, return existing count without API call
          return { storeId, count: existingData.count };
        }
      }

      logger.info('Fetching recent placed and processing order count', { storeId });
      const count = await CustomerOrderService.getRecentPlacedAndProcessingCount(storeId);
      
      return { storeId, count };
    } catch (error: any) {
      logger.error('Failed to fetch recent placed and processing order count', { error, storeId });
      return rejectWithValue({ storeId, error: error.message || 'Failed to fetch count' });
    }
  }
);

// Slice
const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    clearStores: (state) => {
      state.currentStore = null;
      state.currentStoreId = null;
      state.error = null;
      state.stores = [];
      state.currentStoreAdmins = {};
      state.currentStoreAdminsLoading = {};
      state.currentStoreAdminsError = {};
      // Clear app config as well
      state.currentStoreAppConfig = {
        config: null,
        isLoading: false,
        error: null,
        lastUpdated: null,
      };
    },
    setCurrentStore: (state, action: PayloadAction<Store>) => {
      const store = action.payload;
      // Normalize store_type to ensure it's valid, fallback to "General Store"
      store.store_type = normalizeStoreType(store.store_type);
      // Merge store config with defaults (handles null/undefined internally)
      store.store_config = mergeStoreConfig(store.store_config);
      // Merge app config with defaults (handles null/undefined internally)
      store.app_config = mergeAppConfig(store.app_config);
      state.currentStore = store;
      state.currentStoreId = store.id;
    },
    setCurrentStoreId: (state, action: PayloadAction<string | null>) => {
      state.currentStoreId = action.payload;
    },
    clearStoreAdmins: (state, action: PayloadAction<string>) => {
      const storeId = action.payload;
      delete state.currentStoreAdmins[storeId];
      delete state.currentStoreAdminsLoading[storeId];
      delete state.currentStoreAdminsError[storeId];
    },
    // Clear recent placed and processing order count for a store
    clearRecentPlacedAndProcessingOrderCount: (state, action: PayloadAction<string>) => {
      const storeId = action.payload;
      delete state.recentPlacedAndProcessingOrderCounts[storeId];
      delete state.recentPlacedAndProcessingOrderCountsLoading[storeId];
    },

  },
  extraReducers: (builder) => {
    // Stores List (admin only)
    builder
      .addCase(fetchStores.pending, (state) => {
        state.isStoresLoading = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.isStoresLoading = false;
        state.stores = action.payload.map((row: any) => {
          const store = { ...row.store, userRole: row.role };
          // Normalize store_type to ensure it's valid, fallback to "General Store"
          store.store_type = normalizeStoreType(store.store_type);
          // Merge store config with defaults for each store in the list (handles null/undefined internally)
          store.store_config = mergeStoreConfig(store.store_config);
          // Merge app config with defaults for each store in the list (handles null/undefined internally)
          store.app_config = mergeAppConfig(store.app_config);
          return store;
        });
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.isStoresLoading = false;
        state.error = action.error.message || 'Failed to fetch stores';
      });

    // Current Store
    builder
      .addCase(fetchStoreById.pending, (state) => {
        state.isCurrentStoreLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreById.fulfilled, (state, action) => {
        state.isCurrentStoreLoading = false;
        const store = action.payload;

        // Normalize store_type to ensure it's valid, fallback to "General Store"
        store.store_type = normalizeStoreType(store.store_type);

        // Merge store config with defaults (handles null/undefined internally)
        store.store_config = mergeStoreConfig(store.store_config);

        // Merge app config with defaults (handles null/undefined internally)
        store.app_config = mergeAppConfig(store.app_config);
        state.currentStoreAppConfig.config = mergeAppConfig(store.app_config);

        state.currentStore = store;
      })
      .addCase(fetchStoreById.rejected, (state, action) => {
        state.isCurrentStoreLoading = false;
        state.error = action.error.message || 'Failed to fetch store';
        // If the error is due to store not found or invalid input, clear currentStoreId
        if (
          action.error.message &&
          (action.error.message.includes('invalid input syntax for type uuid') ||
            action.error.message.includes('Failed to fetch store') ||
            action.error.message.includes('JSON object requested, multiple (or no) rows returned'))
        ) {
          state.currentStoreId = null;
          state.currentStore = null;
        }
      });

    // Store Operations
    builder
      .addCase(createStore.fulfilled, (state, action) => {
        const store = action.payload;
        // Normalize store_type to ensure it's valid, fallback to "General Store"
        store.store_type = normalizeStoreType(store.store_type);
        // Merge store config with defaults for newly created store (handles null/undefined internally)
        store.store_config = mergeStoreConfig(store.store_config);
        // Merge app config with defaults for newly created store (handles null/undefined internally)
        store.app_config = mergeAppConfig(store.app_config);
        state.stores = [store, ...state.stores];
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        const store = action.payload;
        // Normalize store_type to ensure it's valid, fallback to "General Store"
        store.store_type = normalizeStoreType(store.store_type);
        // Merge store config with defaults for updated store (handles null/undefined internally)
        store.store_config = mergeStoreConfig(store.store_config);
        // Merge app config with defaults for updated store (handles null/undefined internally)
        store.app_config = mergeAppConfig(store.app_config);
        if (state.currentStore?.id === store.id) {
          state.currentStore = store;
        }
      })
      .addCase(deleteStore.pending, (state) => {
        state.isDeletingStore = true;
        state.error = null;
      })
      .addCase(deleteStore.fulfilled, (state, action) => {
        state.isDeletingStore = false;
        // Remove from stores list
        state.stores = state.stores.filter(store => store.id !== action.payload);
        // Clear current store if it was deleted
        if (state.currentStore?.id === action.payload) {
          state.currentStore = null;
          state.currentStoreId = null;
        }
      })
      .addCase(deleteStore.rejected, (state, action) => {
        state.isDeletingStore = false;
        state.error = action.error.message || 'Failed to delete store';
      });

    // Add Store Categories
    builder
      .addCase(addStoreCategories.fulfilled, (state, action) => {
        if (state.currentStore && action.payload.newCategories.length > 0) {
          const existingCategories = (state.currentStore.categories || []) as string[];
          const newCategories = action.payload.newCategories.filter(
            (cat: string) => !existingCategories.includes(cat)
          );
          if (newCategories.length > 0) {
            state.currentStore = {
              ...state.currentStore,
              categories: [...existingCategories, ...newCategories] as string[]
            };
          }
        }
      });

    // Recent Placed and Processing Order Count
    builder
      .addCase(fetchRecentPlacedAndProcessingOrderCount.pending, (state, action) => {
        const storeId = action.meta.arg;
        state.recentPlacedAndProcessingOrderCountsLoading[storeId] = true;
      })
      .addCase(fetchRecentPlacedAndProcessingOrderCount.fulfilled, (state, action) => {
        const { storeId, count } = action.payload;
        state.recentPlacedAndProcessingOrderCountsLoading[storeId] = false;
        // Update state with new count and timestamp
        state.recentPlacedAndProcessingOrderCounts[storeId] = {
          count,
          lastFetched: new Date().toISOString(),
        };
      })
      .addCase(fetchRecentPlacedAndProcessingOrderCount.rejected, (state, action) => {
        const storeId = action.meta.arg;
        state.recentPlacedAndProcessingOrderCountsLoading[storeId] = false;
        // Set count to 0 on error to avoid showing stale data
        if (!state.recentPlacedAndProcessingOrderCounts[storeId]) {
          state.recentPlacedAndProcessingOrderCounts[storeId] = {
            count: 0,
            lastFetched: null,
          };
        }
      });
  },
});

// Actions
export const {
  clearStores,
  setCurrentStore,
  clearStoreAdmins,
  setCurrentStoreId,
  clearRecentPlacedAndProcessingOrderCount,
} = storesSlice.actions;

// Selectors
export const selectCurrentStore = (state: { stores: StoresState }) => state.stores.currentStore;
export const selectCurrentStoreId = (state: { stores: StoresState }) => state.stores.currentStoreId;
export const selectCurrentStoreAppConfig = (state: { stores: StoresState }) => state.stores.currentStoreAppConfig;
export const selectStores = (state: { stores: StoresState }) => state.stores.stores;
export const selectIsStoresLoading = (state: { stores: StoresState }) => state.stores.isStoresLoading;
export const selectIsDeletingStore = (state: { stores: StoresState }) => state.stores.isDeletingStore;
export const selectCurrentStoreAdmins = (storeId: string) => (state: { stores: StoresState }) => state.stores.currentStoreAdmins[storeId] || [];
export const selectCurrentStoreAdminsLoading = (storeId: string) => (state: { stores: StoresState }) => state.stores.currentStoreAdminsLoading[storeId] || false;
export const selectCurrentStoreAdminsError = (storeId: string) => (state: { stores: StoresState }) => state.stores.currentStoreAdminsError[storeId] || null;
export const selectIsCurrentStoreLoading = (state: { stores: StoresState }) => state.stores.isCurrentStoreLoading;
// Selectors for recent placed and processing order count
export const selectRecentPlacedAndProcessingOrderCount = (storeId: string | null | undefined) => (state: { stores: StoresState }) => {
  if (!storeId) return 0;
  return state.stores.recentPlacedAndProcessingOrderCounts[storeId]?.count || 0;
};
export const selectRecentPlacedAndProcessingOrderCountLoading = (storeId: string | null | undefined) => (state: { stores: StoresState }) => {
  if (!storeId) return false;
  return state.stores.recentPlacedAndProcessingOrderCountsLoading[storeId] || false;
};

export default storesSlice.reducer;
